import { EN, ES } from "@/constants/core";
import { AxiosInstance, setSeeriApi } from "@/http";
import { enUS, esCO, esMX } from "@/utils/locale";
import { AxiosRequestConfig } from "axios";
import { getAuth } from "firebase/auth";

import packageJson from "../../package.json";
import { app as firebaseApp, firebaseLogout } from "./firebase";

type SeeriApiParams = {
  baseURL: string;
  accessToken: string;
  country: string;
};

const appHeaders = {
  "x-origin-name": "suppliers",
  "x-origin-platform-version": packageJson.version,
  "x-origin-agent-version": typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
  "x-origin-agent-name": "web",
  "x-origin-language": "es-CO",
};

export const configureSeeriApi = ({ country, baseURL, accessToken }: SeeriApiParams) => {
  const config: AxiosRequestConfig = {
    baseURL,
    headers: appHeaders,
  };

  if (accessToken && config.headers) {
    config.headers["Authorization"] = `Bearer ${accessToken}`;
  }

  if (country && config.headers) {
    const localLang = localStorage.getItem("lang") ?? ES;
    const lang = localLang === EN ? enUS : country === "co" ? esCO : esMX;
    config.headers["x-user-language"] = lang;
    config.headers["x-user-country"] = country;
    config.headers["Accept-Language"] = lang.slice(0, 2);
  }

  const instance = setSeeriApi(config);
  setSeeriApiInterceptors(instance);
};

const setSeeriApiInterceptors = async (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    async (config) => {
      const metricsProxyTarget = import.meta.env.VITE_METRICS_PROXY_TARGET as string | undefined;
      if (metricsProxyTarget && config.url?.startsWith("/api/metrics")) {
        config.baseURL = metricsProxyTarget.replace(/\/$/, "");
        config.url = config.url?.replace("/api/metrics", "");
      }

      const channelsProxyTarget = import.meta.env.VITE_CHANNELS_PROXY_TARGET as string | undefined;
      if (channelsProxyTarget && config.url?.startsWith("/api/channels")) {
        config.baseURL = channelsProxyTarget.replace(/\/$/, "");
        config.url = config.url?.replace("/api/channels", "");
      }

      const taniaProxyTarget = import.meta.env.VITE_TANIA_FUNCTIONS_PROXY_TARGET as
        | string
        | undefined;
      if (taniaProxyTarget && config.url?.startsWith("/api/tania-functions")) {
        config.baseURL = taniaProxyTarget.replace(/\/$/, "");
        config.url = config.url?.replace("/api/tania-functions", "");
      }

      const analyticsProxyTarget = import.meta.env.VITE_ANALYTICS_PROXY_TARGET as
        | string
        | undefined;
      if (analyticsProxyTarget && config.url?.startsWith("/api/analytics-agent")) {
        config.baseURL = analyticsProxyTarget.replace(/\/$/, "");
        config.url = config.url?.replace("/api/analytics-agent", "");
      } else if (analyticsProxyTarget && config.url?.startsWith("/api/analytics")) {
        config.baseURL = analyticsProxyTarget.replace(/\/$/, "");
      }

      // Agents debug: when running the agents service locally, route every
      // /api/agents/* call through it. The local service exposes routes under
      // its own BASE_PATH (=/api/agents/), so we don't strip the prefix.
      const agentsProxyTarget = import.meta.env.VITE_AGENTS_PROXY_TARGET as string | undefined;
      if (agentsProxyTarget && config.url?.startsWith("/api/agents")) {
        config.baseURL = agentsProxyTarget.replace(/\/$/, "");
      }

      // Suppliers debug (TAN-832): same pattern. Local suppliers exposes routes
      // under BASE_PATH=/api/suppliers/ so we don't strip the prefix.
      const suppliersProxyTarget = import.meta.env.VITE_SUPPLIERS_PROXY_TARGET as
        | string
        | undefined;
      if (suppliersProxyTarget && config.url?.startsWith("/api/suppliers")) {
        config.baseURL = suppliersProxyTarget.replace(/\/$/, "");
      }

      // Bandeja v2: when running elasticsearch-gateway locally, route any
      // /api/search/* call through it. The ingress in prod strips the prefix
      // before forwarding, so we do the same here.
      const searchProxyTarget = import.meta.env.VITE_SEARCH_PROXY_TARGET as string | undefined;
      if (searchProxyTarget && config.url?.startsWith("/api/search")) {
        config.baseURL = searchProxyTarget.replace(/\/$/, "");
        config.url = config.url?.replace("/api/search", "/searches");
      }

      if (firebaseApp) {
        const user = getAuth(firebaseApp).currentUser;
        const tokenResult = await user?.getIdTokenResult();
        const accessToken = tokenResult?.token;

        if (accessToken && config.headers) {
          config.headers["Authorization"] = `Bearer ${accessToken}`;
        }

        const supplierId = tokenResult?.claims?.["supplier_id"];
        const claimedUserId =
          tokenResult?.claims?.["seeri_user_id"] ?? tokenResult?.claims?.["user_id"];
        // Backend expects UUID for x-user-id; Firebase uid usually is not UUID.
        const userId = claimedUserId || supplierId;

        if (supplierId && config.headers && !config.headers["x-supplier-id"]) {
          config.headers["x-supplier-id"] = String(supplierId);
          config.headers["x-user-id"] = String(claimedUserId);
        }

        // Inject analytics-specific headers
        if (
          config.url?.startsWith("/api/analytics") ||
          config.url?.startsWith("/api/analytics-agent") ||
          config.url?.includes("analytics")
        ) {
          if (supplierId && config.headers && !config.headers["x-org-id"]) {
            config.headers["x-org-id"] = String(supplierId);
          }

          if (userId && config.headers && !config.headers["x-user-id"]) {
            config.headers["x-user-id"] = String(userId);
          }

          if (config.headers && !config.headers["x-timezone"]) {
            config.headers["x-timezone"] = getTimezone();
          }
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      if (401 === error?.response?.status) {
        firebaseLogout();
      } else {
        return Promise.reject(error);
      }
    }
  );

  return instance;
};

/**
 * Get user's timezone (IANA format)
 * Defaults to America/Bogota for Colombia
 */
function getTimezone(): string {
  try {
    if (typeof Intl !== "undefined") {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz || "America/Bogota";
    }
  } catch {
    // Fall through to default
  }
  return "America/Bogota";
}
